import { CurrencyPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import type { TradeStudyCharts } from '../../../models/trade-study-charts.model';
import { DownloadService } from '../../services/download.service';
import { TradeStudyChartsService } from '../../services/trade-study-charts.service';
import { SettingsService } from '../../settings-page/settings-service/settings.service';
import {
  createCapacityGroupedBarChartOptions,
  createCompletionGroupedBarChartOptions,
  createCostPerPartBarChartOptions,
  createCostPerProcessBarChartOptions,
  createHorizontalStackedBarChartOptions,
  createHorizontalStackedProcessBarChartOptions,
  createPieChartOptions,
  createTimePerPartBarChartOptions,
  createTimePerProcessBarChartOptions,
  readTradeStudyChartColors,
} from './trade-study-chart-options';
import {
  buildTimeBarChartData,
  buildCostBarChartData,
  buildCostCompositionChartData,
  buildPieChartData,
  buildCapacityVsRequiredChartData,
  buildCompletionMonthsChartData,
} from './trade-study-chart-data';
import { buildTradeStudyChartsCsv } from './trade-study-charts-csv';

@Component({
  selector: 'app-trade-study-charts-dialog',
  imports: [
    DialogModule,
    ButtonModule,
    ChartModule,
    FormsModule,
    SelectModule,
    DecimalPipe,
    CurrencyPipe,
  ],
  templateUrl: './trade-study-charts-dialog.component.html',
  styleUrl: './trade-study-charts-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TradeStudyChartsDialogComponent {
  private tradeStudyChartsService = inject(TradeStudyChartsService);
  private downloadService = inject(DownloadService);
  private settingsService = inject(SettingsService);
  private readonly chartColors = readTradeStudyChartColors();

  private loadSeq = 0;

  visible = model(false);
  quoteId = input.required<string>();
  quoteDisplayCode = input.required<string>();

  loadSettled = signal(false);
  charts = signal<TradeStudyCharts | null>(null);
  selectedPartPersistentId = signal<string | null>(null);

  displayCurrencyCode = toSignal(
    this.settingsService.settings$.pipe(
      map((settings) => settings?.organisation_base_currency_code || 'GBP'),
    ),
    { initialValue: 'GBP' },
  );

  readonly capacityGroupedBarChartOptions =
    createCapacityGroupedBarChartOptions(this.chartColors);
  readonly completionGroupedBarChartOptions =
    createCompletionGroupedBarChartOptions(this.chartColors);
  readonly pieChartOptions = createPieChartOptions(this.chartColors);

  readonly timePerPartBarChartOptions = computed(() =>
    createTimePerPartBarChartOptions(
      this.chartColors,
      this.displayCurrencyCode(),
    ),
  );
  readonly costPerPartBarChartOptions = computed(() =>
    createCostPerPartBarChartOptions(
      this.chartColors,
      this.displayCurrencyCode(),
    ),
  );
  readonly timePerProcessBarChartOptions = computed(() =>
    createTimePerProcessBarChartOptions(
      this.chartColors,
      this.displayCurrencyCode(),
    ),
  );
  readonly costPerProcessBarChartOptions = computed(() =>
    createCostPerProcessBarChartOptions(
      this.chartColors,
      this.displayCurrencyCode(),
    ),
  );
  readonly horizontalStackedBarChartOptions = computed(() =>
    createHorizontalStackedBarChartOptions(
      this.chartColors,
      this.displayCurrencyCode(),
    ),
  );
  readonly horizontalStackedProcessBarChartOptions = computed(() =>
    createHorizontalStackedProcessBarChartOptions(
      this.chartColors,
      this.displayCurrencyCode(),
    ),
  );

  readonly selectedProcessRows = computed(
    (): TradeStudyCharts['time_per_part'] => {
      const chart = this.charts();
      const id = this.selectedPartPersistentId();
      if (!chart || !id) return [];
      return chart.processes_by_part[id] ?? [];
    },
  );

  readonly selectedCostCompositionRows = computed(
    (): TradeStudyCharts['cost_composition_by_process'] => {
      const c = this.charts();
      const id = this.selectedPartPersistentId();
      if (!c || !id) return [];
      return c.cost_composition_by_part[id] ?? [];
    },
  );

  readonly selectedPartName = computed(
    () =>
      this.charts()?.parts.find(
        (p) => p.persistent_id === this.selectedPartPersistentId(),
      )?.name ?? 'selected part',
  );

  readonly horizontalPartChartHeightPx = computed(() => {
    const n = Math.max(this.charts()?.time_per_part?.length ?? 1, 1);
    return Math.min(520, Math.max(200, 36 * n + 56));
  });

  readonly horizontalProcessChartHeightPx = computed(() => {
    const n = Math.max(this.selectedProcessRows().length, 1);
    return Math.min(520, Math.max(200, 36 * n + 56));
  });

  readonly horizontalProcessCompositionChartHeightPx = computed(() => {
    const n = Math.max(this.selectedCostCompositionRows().length, 1);
    return Math.min(560, Math.max(240, 40 * n + 80));
  });

  readonly horizontalCompositionChartHeightPx = computed(() => {
    const n = Math.max(
      this.charts()?.cost_composition_by_process?.length ?? 1,
      1,
    );
    return Math.min(560, Math.max(240, 40 * n + 80));
  });

  readonly timePerPartChartData = computed(() =>
    buildTimeBarChartData(this.charts()?.time_per_part ?? [], this.chartColors),
  );

  readonly costPerPartChartData = computed(() =>
    buildCostBarChartData(
      this.charts()?.cost_per_part ?? [],
      this.displayCurrencyCode(),
      this.chartColors,
    ),
  );

  readonly timePerProcessChartData = computed(() =>
    buildTimeBarChartData(this.selectedProcessRows(), this.chartColors),
  );

  readonly costPerProcessChartData = computed(() =>
    buildCostBarChartData(
      this.selectedProcessRows(),
      this.displayCurrencyCode(),
      this.chartColors,
    ),
  );

  readonly timeDistributionPieData = computed(() =>
    buildPieChartData(this.charts()?.time_distribution ?? [], this.chartColors),
  );

  readonly capacityVsRequiredChartData = computed(() =>
    buildCapacityVsRequiredChartData(
      this.charts()?.capacity_vs_required_hours ?? [],
      this.chartColors,
    ),
  );

  readonly completionMonthsChartData = computed(() =>
    buildCompletionMonthsChartData(
      this.charts()?.completion_time_by_shift ?? [],
      this.chartColors,
    ),
  );

  readonly costCompositionChartData = computed(() =>
    buildCostCompositionChartData(
      this.charts()?.cost_composition_by_process ?? [],
      this.chartColors,
    ),
  );

  readonly costCompositionByProcessChartData = computed(() =>
    buildCostCompositionChartData(
      this.selectedCostCompositionRows(),
      this.chartColors,
    ),
  );

  async onDialogShow(): Promise<void> {
    const loadId = ++this.loadSeq;

    try {
      const charts = await this.tradeStudyChartsService.getTradeStudyCharts(
        this.quoteId(),
      );

      if (loadId !== this.loadSeq) {
        return;
      }

      this.charts.set(charts);
      this.selectedPartPersistentId.set(charts.parts[0]?.persistent_id ?? null);
    } catch {
      if (loadId !== this.loadSeq) {
        return;
      }

      this.charts.set(null);
      this.selectedPartPersistentId.set(null);
    } finally {
      if (loadId === this.loadSeq) {
        this.loadSettled.set(true);
      }
    }
  }

  close(): void {
    this.visible.set(false);
  }

  onDialogHide(): void {
    this.loadSeq++;
    this.loadSettled.set(false);
    this.charts.set(null);
    this.selectedPartPersistentId.set(null);
  }

  exportToCsv(): void {
    const c = this.charts();
    if (!c) return;

    const csv = buildTradeStudyChartsCsv(c, {
      currencyCode: this.displayCurrencyCode(),
      selectedPartPersistentId: this.selectedPartPersistentId(),
    });

    this.downloadService.downloadText(csv, {
      defaultFilename: `${this.quoteDisplayCode()} Charts.csv`,
      mimeType: 'text/csv;charset=utf-8',
    });
  }
}
